import axios, { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import type { ApiError } from './types';
import { getAuthToken } from '../../lib/auth';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken();

  if (token && config.headers) {
    (config.headers as AxiosRequestHeaders)['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

// Flag para prevenir múltiples redirecciones simultáneas
let isRedirecting = false;

axiosInstance.interceptors.response.use(
  (response) => response.data ?? response,
  (error) => {
    const status: number | undefined = error.response?.status;
    
    // Manejar específicamente errores 401 (Unauthorized)
    if (status === 403) {
      if (!isRedirecting) {
        isRedirecting = true;
        
        try {
          // Limpiar sessionStorage
          sessionStorage.removeItem("authUser");
          
          // Redirigir a login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } catch (err) {
          console.error('Error durante logout automático:', err);
        }
        
        // Resetear el flag después de un tiempo
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }
      
      return Promise.reject('Sesión expirada');
    }

    const messageMap: Record<number, string> = {
      400: error.response?.data?.message ?? 'Solicitud incorrecta',
      403: 'No tienes permisos para realizar esta acción',
      404: 'El recurso solicitado no fue encontrado',
      500: 'Error interno del servidor',
    };

    const apiError: ApiError = {
      message: (status && messageMap[status]) ?? error.response?.data?.message ?? error.message ?? 'Error inesperado',
      status,
      raw: error.response?.data,
    };

    return Promise.reject(apiError);
  },
);

const apiClient = {
  /**
   * GET request. Pass a typed params object — Axios serializes it into query string.
   * Accepts any object type to avoid forcing an index signature on caller interfaces.
   */
  get<T>(url: string, params?: object, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.get<T, T>(url, { params, ...config });
  },

  post<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post<T, T>(url, data, config);
  },

  put<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.put<T, T>(url, data, config);
  },

  patch<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.patch<T, T>(url, data, config);
  },

  delete<T>(url: string): Promise<T> {
    return axiosInstance.delete<T, T>(url);
  },

  /**
   * Downloads a file (blob response). POST with request body.
   */
  download(url: string, data?: unknown): Promise<Blob> {
    return axiosInstance.post<Blob, Blob>(url, data, {
      responseType: 'blob',
    } as AxiosRequestConfig);
  },
};

export default apiClient;
